import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Send, Bot, User, Loader, AlertCircle, MessageSquarePlus, ChevronDown, ChevronRight, Wrench, Trash2, ExternalLink, Brain } from 'lucide-react';
import { useAI } from '../../hooks/useAI';
import { aiAPI, todosAPI, eventsAPI } from '../../services/api';
import type { AIModel, AIConversationSummary, AIToolResult } from '../../services/api';
import { LocalAgentConnection } from './LocalAgentConnection';

interface EntityRef {
  type: 'task' | 'event';
  id: string;
  title: string;
}

function extractEntityRefs(toolResults: AIToolResult[]): EntityRef[] {
  const refs: EntityRef[] = [];
  const seen = new Set<string>();

  const add = (type: 'task' | 'event', id: string, title: string) => {
    const key = `${type}:${id}`;
    if (id && title && !seen.has(key)) {
      seen.add(key);
      refs.push({ type, id, title });
    }
  };

  for (const tr of toolResults) {
    if (!tr.success) continue;
    const r = tr.result as Record<string, unknown>;

    // Delegate tool — recurse into inner tool_results
    if (tr.tool === 'delegate') {
      const innerResults = (r.tool_results as AIToolResult[]) || [];
      const innerRefs = extractEntityRefs(innerResults);
      for (const ref of innerRefs) add(ref.type, ref.id, ref.title);
      continue;
    }

    // Single task
    const task = r.task as Record<string, unknown> | undefined;
    if (task?.id && task?.title) {
      add('task', task.id as string, task.title as string);
    }

    // Task list (search_tasks)
    const tasks = r.tasks as Record<string, unknown>[] | undefined;
    if (Array.isArray(tasks)) {
      for (const t of tasks) {
        if (t.id && t.title) add('task', t.id as string, t.title as string);
      }
    }

    // Single event
    const event = r.event as Record<string, unknown> | undefined;
    if (event?.id && event?.title) {
      add('event', event.id as string, event.title as string);
    }

    // Event list (get_events_for_date)
    const events = r.events as Record<string, unknown>[] | undefined;
    if (Array.isArray(events)) {
      for (const e of events) {
        if (e.id && e.title) add('event', e.id as string, e.title as string);
      }
    }

    // Comment references a task
    const comment = r.comment as Record<string, unknown> | undefined;
    if (comment?.task_id && comment?.task_title) {
      add('task', comment.task_id as string, comment.task_title as string);
    }

    // get_task returns the task directly at the top level
    if (r.id && r.title && !r.task && !r.event && tr.tool === 'get_task') {
      add('task', r.id as string, r.title as string);
    }
  }

  return refs;
}

const ThinkingBlock: React.FC<{ thinking: string }> = ({ thinking }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div className="mb-1">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Brain className="w-3 h-3" />
        <span>{isExpanded ? 'Hide thinking' : 'Show thinking'}</span>
      </button>
      {isExpanded && (
        <div className="mt-1 px-2 py-1.5 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600 whitespace-pre-wrap max-h-48 overflow-y-auto">
          {thinking}
        </div>
      )}
    </div>
  );
};

interface AIChatProps {
  isOpen?: boolean;
  onClose?: () => void;
  className?: string;
  compact?: boolean;
  allTodos?: unknown[];
  initialConversationId?: string;
  taskId?: string;
  taskTitle?: string;
  focusSessionId?: string;
  onSendMessageRef?: React.MutableRefObject<((message: string) => void) | null>;
}

export const AIChat: React.FC<AIChatProps> = ({
  isOpen,
  onClose,
  className = '',
  compact = false,
  initialConversationId,
  taskId,
  taskTitle,
  focusSessionId,
  onSendMessageRef,
}) => {
  const {
    messages,
    isLoading,
    progress,
    error,
    conversationId,
    model,
    setModel,
    sendMessage,
    loadConversation,
    clearMessages,
  } = useAI({ taskId, focusSessionId });

  const [inputMessage, setInputMessage] = useState('');
  const [models, setModels] = useState<AIModel[]>([]);
  const [conversations, setConversations] = useState<AIConversationSummary[]>([]);
  const [showConversations, setShowConversations] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Load initial conversation if provided
  useEffect(() => {
    if (initialConversationId) {
      loadConversation(initialConversationId);
    }
  }, [initialConversationId, loadConversation]);

  // Load available models on mount
  useEffect(() => {
    aiAPI.getModels().then(data => {
      setModels(data.models);
    }).catch(() => {});
  }, []);

  const handleEntityClick = useCallback(async (ref: EntityRef) => {
    try {
      if (ref.type === 'task') {
        const todo = await todosAPI.get(ref.id);
        window.dispatchEvent(new CustomEvent('openTaskFromWidget', { detail: { todo } }));
      } else {
        const event = await eventsAPI.get(ref.id);
        window.dispatchEvent(new CustomEvent('openEventFromWidget', { detail: { event } }));
      }
    } catch {
      // Entity may have been deleted
    }
  }, []);

  // Expose sendMessage to parent via ref (for quick actions)
  useEffect(() => {
    if (onSendMessageRef) {
      onSendMessageRef.current = (message: string) => { sendMessage(message); };
    }
    return () => {
      if (onSendMessageRef) onSendMessageRef.current = null;
    };
  }, [onSendMessageRef, sendMessage]);

  // If isOpen is provided and false, don't render
  if (isOpen !== undefined && !isOpen) return null;

  const loadConversationList = async () => {
    if (showConversations) {
      setShowConversations(false);
      return;
    }
    try {
      const convs = await aiAPI.getConversations();
      setConversations(convs);
      setShowConversations(true);
    } catch {
      // silently fail
    }
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || isLoading) return;
    const msg = inputMessage;
    setInputMessage('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    const sent = await sendMessage(msg);
    if (!sent) setInputMessage(msg);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = () => {
    clearMessages();
    setShowConversations(false);
    inputRef.current?.focus();
  };

  const handleSelectConversation = async (convId: string) => {
    await loadConversation(convId);
    setShowConversations(false);
  };

  const handleDeleteConversation = async (e: React.MouseEvent, convId: string) => {
    e.stopPropagation();
    try {
      await aiAPI.deleteConversation(convId);
      setConversations(prev => prev.filter(c => c.id !== convId));
      if (conversationId === convId) {
        clearMessages();
      }
    } catch {
      // silently fail
    }
  };

  const toolDisplayName = (tool: string) => {
    const names: Record<string, string> = {
      search_tasks: 'Searched tasks',
      get_task: 'Retrieved task details',
      create_task: 'Created task',
      edit_task: 'Updated task',
      delete_task: 'Deleted task',
      get_events_for_date: 'Checked schedule',
      create_event: 'Created event',
      create_event_from_todo: 'Scheduled task',
      edit_event: 'Updated event',
      delete_event: 'Deleted event',
      add_comment: 'Added comment',
      web_search: 'Searched the web',
      search_history: 'Searched history',
      get_conversation: 'Retrieved conversation',
      search_documents: 'Searched documents',
      get_document: 'Retrieved document',
    };
    return names[tool] || tool;
  };

  const templateDisplayName = (template: string) => {
    const names: Record<string, string> = {
      task_executor: 'Managing tasks',
      event_executor: 'Managing events',
      researcher: 'Researching',
      web_researcher: 'Searching the web',
      summarizer: 'Summarizing',
    };
    return names[template] || template;
  };

  const renderToolResults = (toolResults: AIToolResult[]) => {
    const entityRefs = extractEntityRefs(toolResults);

    return (
      <div className="space-y-1.5">
        {toolResults.map((result, idx) => {
          // Delegate tool — render as hierarchy with inner tools
          if (result.tool === 'delegate') {
            const r = result.result as Record<string, unknown>;
            const template = (r.template as string) || '';
            const innerResults = (r.tool_results as AIToolResult[]) || [];
            const innerToolsSummary = (r.tools_used as Array<{tool: string; success: boolean}>) || [];

            return (
              <div key={idx} className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <Wrench className="w-3 h-3 flex-shrink-0" />
                  <span className="font-medium">{templateDisplayName(template)}</span>
                  {result.success ? (
                    <span className="text-green-600 dark:text-green-400">&#10003;</span>
                  ) : (
                    <span className="text-red-500 dark:text-red-400">&#10007;</span>
                  )}
                </div>
                {innerToolsSummary.length > 0 && (
                  <div className="ml-4 pl-2 border-l border-gray-200 dark:border-gray-600 space-y-0.5">
                    {innerToolsSummary.map((inner, iIdx) => (
                      <div
                        key={iIdx}
                        className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500"
                      >
                        <span>{toolDisplayName(inner.tool)}</span>
                        {inner.success ? (
                          <span className="text-green-600 dark:text-green-400">&#10003;</span>
                        ) : (
                          <span className="text-red-500 dark:text-red-400">&#10007;</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Entity refs from inner results */}
                {innerResults.length > 0 && (() => {
                  const innerRefs = extractEntityRefs(innerResults);
                  return innerRefs.length > 0 ? (
                    <div className="ml-4 pl-2 border-l border-gray-200 dark:border-gray-600">
                      <div className="flex flex-wrap gap-1 pt-0.5">
                        {innerRefs.map((ref) => (
                          <button
                            key={`${ref.type}:${ref.id}`}
                            onClick={() => handleEntityClick(ref)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors cursor-pointer"
                            title={`Open ${ref.type}: ${ref.title}`}
                          >
                            <ExternalLink className="w-2.5 h-2.5" />
                            <span className="truncate max-w-[150px]">{ref.title}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            );
          }

          // Regular tool — flat display
          return (
            <div
              key={idx}
              className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
            >
              <Wrench className="w-3 h-3 flex-shrink-0" />
              <span>{toolDisplayName(result.tool)}</span>
              {result.success ? (
                <span className="text-green-600 dark:text-green-400">&#10003;</span>
              ) : (
                <span className="text-red-500 dark:text-red-400">&#10007;</span>
              )}
            </div>
          );
        })}
        {entityRefs.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-0.5">
            {entityRefs.map((ref) => (
              <button
                key={`${ref.type}:${ref.id}`}
                onClick={() => handleEntityClick(ref)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors cursor-pointer"
                title={`Open ${ref.type}: ${ref.title}`}
              >
                <ExternalLink className="w-2.5 h-2.5" />
                <span className="truncate max-w-[150px]">{ref.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const markdownComponents: Components = {
    p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
    ul: ({ children }) => <ul className="list-disc pl-4 mb-1">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-4 mb-1">{children}</ol>,
    li: ({ children }) => <li className="mb-0.5">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    code: ({ children, className }) => {
      const isBlock = className?.includes('language-');
      if (isBlock) {
        return (
          <code className={`block bg-gray-800 text-gray-100 rounded px-2 py-1 text-xs overflow-x-auto ${className || ''}`}>
            {children}
          </code>
        );
      }
      return (
        <code className="bg-gray-200 dark:bg-gray-600 rounded px-1 py-0.5 text-xs">
          {children}
        </code>
      );
    },
    pre: ({ children }) => <pre className="mb-1 last:mb-0">{children}</pre>,
    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
        {children}
      </a>
    ),
    h1: ({ children }) => <h1 className="text-base font-bold mb-1">{children}</h1>,
    h2: ({ children }) => <h2 className="text-sm font-bold mb-1">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-2 border-gray-300 dark:border-gray-500 pl-2 italic my-1">{children}</blockquote>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto my-1">
        <table className="text-xs border-collapse">{children}</table>
      </div>
    ),
    th: ({ children }) => <th className="border border-gray-300 dark:border-gray-500 px-2 py-0.5 font-semibold">{children}</th>,
    td: ({ children }) => <td className="border border-gray-300 dark:border-gray-500 px-2 py-0.5">{children}</td>,
  };

  const headerHeight = compact ? 'py-2' : 'p-4';
  const bodyPadding = compact ? 'p-2' : 'p-4';

  return (
    <div className={`flex flex-col h-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm ${className}`}>
      {/* Header */}
      <LocalAgentConnection />
      <div className={`flex items-center justify-between ${headerHeight} px-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-lg`}>
        <div className="flex items-center gap-2 min-w-0">
          <Bot className="h-4 w-4 text-blue-600 flex-shrink-0" />
          {taskTitle ? (
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate" title={taskTitle}>
              AI: {taskTitle}
            </h3>
          ) : (
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100">AI Assistant</h3>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Model picker */}
          <select
            aria-label="AI model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            {models.map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
            {models.length === 0 && <option value={model}>Claude Sonnet 4.6</option>}
          </select>
          {/* Conversations button — hidden in task mode */}
          {!taskId && (
            <button
              onClick={loadConversationList}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500"
              title="Past conversations"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
          {/* New conversation — hidden in task mode */}
          {!taskId && (
            <button
              onClick={handleNewConversation}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500"
              title="New conversation"
            >
              <MessageSquarePlus className="w-4 h-4" />
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close AI Assistant"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors ml-1"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* Conversation list dropdown */}
      {showConversations && (
        <div className="border-b border-gray-200 dark:border-gray-700 max-h-48 overflow-y-auto bg-white dark:bg-gray-800">
          {conversations.length === 0 ? (
            <p className="text-xs text-gray-500 p-3">No past conversations</p>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.id}
                className="flex items-center border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700 group"
              >
                <button
                  onClick={() => handleSelectConversation(conv.id)}
                  className="flex-1 text-left px-3 py-2 text-xs min-w-0"
                >
                  <div className="font-medium text-gray-800 dark:text-gray-200 truncate">
                    {conv.title || 'Untitled conversation'}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    {conv.message_count} messages
                    {conv.model_provider && ` · ${conv.model_provider}`}
                  </div>
                </button>
                <button
                  onClick={(e) => handleDeleteConversation(e, conv.id)}
                  className="p-1.5 mr-1 rounded text-gray-400 opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                  title="Delete conversation"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {/* Messages */}
      <div ref={messagesContainerRef} className={`flex-1 overflow-y-auto ${bodyPadding} space-y-3 min-h-0`}>
        {messages.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <Bot className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">
              {taskId
                ? 'Ask me anything about this task, or use the quick actions above.'
                : 'Hi! I can help manage your tasks. Try asking me to search, create, or update tasks.'}
            </p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`flex max-w-[85%] ${
                message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
              }`}
            >
              <div className={`flex-shrink-0 ${message.role === 'user' ? 'ml-2' : 'mr-2'}`}>
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center ${
                    message.role === 'user'
                      ? 'bg-blue-600'
                      : message.thinking ? 'bg-purple-200 dark:bg-purple-800' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                >
                  {message.role === 'user' ? (
                    <User className="h-3.5 w-3.5 text-white" />
                  ) : (
                    <Bot className={`h-3.5 w-3.5 ${message.thinking ? 'text-purple-700 dark:text-purple-300' : 'text-gray-600 dark:text-gray-300'}`} />
                  )}
                </div>
              </div>
              <div
                className={`rounded-lg px-3 py-2 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}
              >
                {message.role === 'assistant' && message.thinking && (
                  <ThinkingBlock thinking={message.thinking} />
                )}
                {message.tool_results && message.tool_results.length > 0 && renderToolResults(message.tool_results)}
                {message.content && (
                  message.role === 'assistant' ? (
                    <div className={`text-sm prose-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${message.tool_results?.length || message.thinking ? 'mt-2 pt-2 border-t border-gray-200 dark:border-gray-600' : ''}`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className={`text-sm whitespace-pre-wrap ${message.tool_results?.length ? 'mt-2 pt-2 border-t border-gray-200 dark:border-gray-600' : ''}`}>
                      {message.content}
                    </p>
                  )
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 mr-2 flex items-center justify-center">
                <Bot className="h-3.5 w-3.5 text-gray-600 dark:text-gray-300" />
              </div>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <Loader className="h-3.5 w-3.5 animate-spin text-gray-600 dark:text-gray-300" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {progress || 'Loading conversation…'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Error */}
      {error && (
        <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-red-50 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-3.5 w-3.5" />
            <span className="text-xs">{error}</span>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={taskId ? "Ask about this task..." : "Ask about your tasks..."}
            rows={1}
            className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none overflow-hidden"
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
            }}
          />
          <button
            onClick={handleSend}
            aria-label="Send message"
            disabled={!inputMessage.trim() || isLoading}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
